import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import CaisseController from '../controllers/caisse-controller';

const fmtMoney = (v) => `${Number(v || 0).toFixed(3)} TND`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

// Lists every commande of a caisse and lets the user delete them.
// Deleting restores stock and recomputes the caisse total on the backend.
const CaisseCommandesModal = ({ isOpen, onClose, caisse = null, canDelete = false, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [details, setDetails] = useState({});
  const [deletingLineId, setDeletingLineId] = useState(null);

  const cloture = Boolean(caisse?.cloture);
  const allowDelete = canDelete;

  const loadOrders = useCallback(async () => {
    if (!caisse?.id) return;
    setLoading(true);
    try {
      let all = [];
      let p = 1;
      let maxPage = 1;
      do {
        const r = await CaisseController.paginateCommandes(caisse.id, p, 200);
        if (r && r.list) {
          all = [...all, ...r.list];
          maxPage = r.maxPage || 1;
        } else break;
        p++;
      } while (p <= maxPage);
      setOrders(all);
    } catch (e) {
      console.error('Load commandes error:', e);
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  }, [caisse]);

  useEffect(() => {
    if (isOpen) {
      setOrders([]);
      setExpandedId(null);
      setDetails({});
      loadOrders();
    }
  }, [isOpen, loadOrders]);

  const loadDetails = useCallback(async (orderId) => {
    setDetailLoadingId(orderId);
    try {
      const r = await CaisseController.getCommande(orderId);
      const lines = (r && (r.line || r.Line)) || [];
      setDetails((prev) => ({ ...prev, [orderId]: lines }));
    } catch (e) {
      console.error('Load commande details error:', e);
      toast.error('Erreur lors du chargement du détail');
    } finally {
      setDetailLoadingId(null);
    }
  }, []);

  const toggleExpand = (orderId) => {
    if (expandedId === orderId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(orderId);
    if (!details[orderId]) loadDetails(orderId);
  };

  const handleDelete = async (order) => {
    if (!order?.id) return;
    if (!window.confirm(`Supprimer la commande ${order.reference || '#' + order.id} ? Le stock sera restauré et le total recalculé.`)) return;
    setDeletingId(order.id);
    try {
      await CaisseController.deleteCommande(order.id);
      toast.success('Commande supprimée');
      await loadOrders();
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Delete commande error:', err);
      toast.error(err?.response?.data?.message || err?.response?.data || 'Erreur lors de la suppression de la commande.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteLine = async (order, line) => {
    if (!line?.id) return;
    const label = line.product?.name || line.productVariant?.name || `#${line.id}`;
    if (!window.confirm(`Supprimer la ligne « ${label} » ? Le stock sera restauré et le total recalculé.`)) return;
    setDeletingLineId(line.id);
    try {
      await CaisseController.deleteCommandeLine(line.id);
      toast.success('Ligne supprimée');
      await loadDetails(order.id);
      await loadOrders();
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Delete line error:', err);
      toast.error(err?.response?.data?.message || err?.response?.data || 'Erreur lors de la suppression de la ligne.');
    } finally {
      setDeletingLineId(null);
    }
  };

  if (!isOpen) return null;

  const total = orders
    .filter((o) => o.etat !== 'canceled')
    .reduce((s, o) => s + (o.montantPaye || 0), 0);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-6">
        <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity" onClick={onClose} />

        <div className="relative bg-white rounded-2xl shadow-2xl z-10 w-full max-w-3xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">Commandes — Caisse #{caisse?.id}</h3>
              <p className="text-xs text-white/80">
                {caisse?.depot?.name || '—'} • {cloture ? 'Clôturée' : 'Ouverte'}
              </p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white transition">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="p-10 flex flex-col items-center">
                <svg className="animate-spin h-8 w-8 text-emerald-600 mb-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <p className="text-gray-600 text-sm">Chargement des commandes…</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="p-10 text-center text-gray-500">Aucune commande pour cette caisse.</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 w-8"></th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Réf</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Client</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Total</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Payé</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase">État</th>
                    {allowDelete && <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Action</th>}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {orders.map((o, i) => {
                    const isPre = (o.etat === 'pending') || ((o.totalTTC || 0) > (o.montantPaye || 0) + 0.0001);
                    const isEch = o.etat === 'echange';
                    const isCanceled = o.etat === 'canceled';
                    const client = `${o.clientFirstName || ''} ${o.clientLastName || ''}`.trim();
                    const isExpanded = expandedId === o.id;
                    const lines = details[o.id];
                    const colSpan = allowDelete ? 9 : 8;
                    return (
                      <React.Fragment key={o.id || i}>
                      <tr className={`hover:bg-gray-50 ${isCanceled ? 'opacity-50' : ''}`}>
                        <td className="px-2 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => toggleExpand(o.id)}
                            title={isExpanded ? 'Masquer le détail' : 'Voir le détail'}
                            className="text-gray-400 hover:text-gray-700"
                          >
                            <svg className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">{i + 1}</td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900">{o.reference || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{fmtDate(o.dateCommande)}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{client || '—'}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700">{fmtMoney(o.totalTTC)}</td>
                        <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900">{fmtMoney(o.montantPaye)}</td>
                        <td className="px-4 py-2 text-center">
                          {isCanceled ? (
                            <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-gray-100 text-gray-500">Annulée</span>
                          ) : isEch ? (
                            <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-indigo-100 text-indigo-700">Échange</span>
                          ) : isPre ? (
                            <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-700">Précommande</span>
                          ) : (
                            <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-emerald-100 text-emerald-700">Payée</span>
                          )}
                        </td>
                        {allowDelete && (
                          <td className="px-4 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => handleDelete(o)}
                              disabled={deletingId === o.id}
                              title="Supprimer cette commande"
                              className="inline-flex items-center text-red-600 hover:text-red-800 disabled:opacity-40"
                            >
                              {deletingId === o.id ? (
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                              ) : (
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              )}
                            </button>
                          </td>
                        )}
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={colSpan} className="px-6 py-3">
                            {detailLoadingId === o.id && !lines ? (
                              <div className="text-sm text-gray-500 py-2">Chargement du détail…</div>
                            ) : !lines || lines.length === 0 ? (
                              <div className="text-sm text-gray-500 py-2">Aucune ligne.</div>
                            ) : (
                              <table className="min-w-full">
                                <thead>
                                  <tr className="text-[11px] text-gray-500 uppercase">
                                    <th className="px-3 py-1 text-left">Article</th>
                                    <th className="px-3 py-1 text-right">Qté</th>
                                    <th className="px-3 py-1 text-right">P.U.</th>
                                    <th className="px-3 py-1 text-right">Total</th>
                                    {allowDelete && <th className="px-3 py-1 text-right"></th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {lines.map((l) => (
                                    <tr key={l.id} className="border-t border-gray-200">
                                      <td className="px-3 py-1.5 text-sm text-gray-800">
                                        {l.product?.name || l.productVariant?.name || `#${l.productId}`}
                                        {l.productVariant?.name && l.product?.name ? <span className="text-gray-500"> — {l.productVariant.name}</span> : null}
                                      </td>
                                      <td className="px-3 py-1.5 text-sm text-right text-gray-700">{l.quantite}</td>
                                      <td className="px-3 py-1.5 text-sm text-right text-gray-700">{fmtMoney(l.prixUnitaire)}</td>
                                      <td className="px-3 py-1.5 text-sm text-right font-medium text-gray-900">{fmtMoney(l.totalTTC)}</td>
                                      {allowDelete && (
                                        <td className="px-3 py-1.5 text-right">
                                          <button
                                            type="button"
                                            onClick={() => handleDeleteLine(o, l)}
                                            disabled={deletingLineId === l.id}
                                            title="Supprimer cette ligne"
                                            className="inline-flex items-center text-red-600 hover:text-red-800 disabled:opacity-40"
                                          >
                                            {deletingLineId === l.id ? (
                                              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                            ) : (
                                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            )}
                                          </button>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{orders.filter((o) => o.etat !== 'canceled').length}</span> commande(s) •
              Total encaissé : <span className="font-semibold text-gray-900">{fmtMoney(total)}</span>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CaisseCommandesModal;
